import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import NavButtons from './NavButtons';
import VasePreview from '../common/VasePreview';
import { BOUQUET_SIZES } from '../../lib/pricing';

const ROSE_COLORS = [
  { name: 'Red',    image: '/images/red-roses.png' },
  { name: 'Pink',   image: '/images/pink-rose.png' },
  { name: 'White',  image: '/images/white-roses.png' },
  { name: 'Yellow', image: '/images/yellow-roses.png' },
];

export default function Step1Arrangement({ formData, updateFormData, onNext, onBack }) {
  const [showModal, setShowModal] = useState(false);

  const isNextDay = formData.delivery_type === 'next_day';

  useEffect(() => {
    const updates = {};
    // Default to 18-rose (most popular) if nothing selected
    if (!formData.vase_size) {
      updates.vase_size = BOUQUET_SIZES[1];
    }
    // For next-day, lock to Red. For scheduled, default to Red if nothing chosen yet.
    if (!formData.flower_type) {
      updates.flower_type = 'Red';
    }
    if (isNextDay && formData.flower_type !== 'Red') {
      updates.flower_type = 'Red';
    }
    if (Object.keys(updates).length) updateFormData(updates);
  }, [isNextDay]);

  const isValid = !!formData.flower_type && !!formData.vase_size;
  const canPreview = formData.design && formData.flower_type;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-10"
    >
      <div className="text-center space-y-3">
        <h2 className="text-4xl font-serif text-charcoal">Choose Your Bouquet</h2>
        <p className="text-sage-500 text-lg font-medium max-w-2xl mx-auto">
          {isNextDay
            ? 'Every arrangement includes premium red roses. Red is the only color available for next-day delivery.'
            : 'Hand-wrapped and delivered fresh. Choose your size and rose color.'}
        </p>
      </div>

      {/* ── Bouquet size picker ───────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {BOUQUET_SIZES.map((size) => {
          const isSelected = formData.vase_size?.name === size.name;
          return (
            <motion.button
              key={size.name}
              whileHover={{ y: -4 }}
              onClick={() => updateFormData({ vase_size: size })}
              className={`relative rounded-3xl p-6 text-left transition-all duration-300 border-2 ${
                isSelected
                  ? 'border-blush-300 bg-blush-50/30 shadow-glow ring-4 ring-blush-100'
                  : 'border-sage-100 bg-white hover:border-blush-200 hover:shadow-soft'
              }`}
            >
              {size.badge && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blush-400 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full shadow-sm whitespace-nowrap">
                  {size.badge}
                </span>
              )}
              <div className="flex justify-between items-start mb-2">
                <span className="font-serif text-2xl text-charcoal">{size.name}</span>
                {isSelected && (
                  <div className="w-6 h-6 bg-blush-300 text-white rounded-full flex items-center justify-center shrink-0 ml-2">
                    <SafeIcon name="Check" className="w-3 h-3" />
                  </div>
                )}
              </div>
              <p className="text-sm text-sage-400 mb-4 leading-relaxed">{size.desc}</p>
              <span className="font-serif text-3xl text-charcoal">${size.price}</span>
            </motion.button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-8 glass-panel p-6 md:p-10 order-1">
          <div className="field-group">
            <div className="flex justify-between items-end mb-4">
              <label className="form-label">Color Selection</label>
              {formData.flower_type && (
                <span className="text-xs font-bold text-blush-400 uppercase tracking-widest bg-blush-50 px-3 py-1 rounded-full border border-blush-100">
                  Selected: {formData.flower_type}
                </span>
              )}
            </div>

            {isNextDay ? (
              /* ── Next-day: Red locked ── */
              <div className="flex flex-col items-center gap-4">
                <div className="relative rounded-3xl border-2 border-blush-300 shadow-glow bg-blush-50/10 p-4 flex flex-col items-center gap-3 w-40">
                  <div className="aspect-square w-full">
                    <img src="/images/red-roses.png" alt="Red roses" className="w-full h-full object-contain" />
                  </div>
                  <span className="text-sm font-bold text-blush-500">Red</span>
                  <div className="absolute top-2 right-2 bg-blush-300 text-white p-1 rounded-full shadow-sm">
                    <SafeIcon name="Check" className="w-3 h-3" />
                  </div>
                </div>
                <p className="text-xs text-sage-400 text-center max-w-xs leading-relaxed">
                  Only red roses are available for next-day delivery.{' '}
                </p>
              </div>
            ) : (
              /* ── Scheduled: all 4 colors selectable ── */
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {ROSE_COLORS.map((color) => {
                  const isSelected = formData.flower_type === color.name;
                  return (
                    <motion.button
                      key={color.name}
                      whileHover={{ y: -3 }}
                      onClick={() => updateFormData({ flower_type: color.name })}
                      className={`relative rounded-3xl border-2 p-4 flex flex-col items-center gap-3 transition-all duration-200 ${
                        isSelected
                          ? 'border-blush-300 bg-blush-50/20 shadow-glow ring-4 ring-blush-100'
                          : 'border-sage-100 bg-white hover:border-blush-200 hover:shadow-soft'
                      }`}
                    >
                      <div className="aspect-square w-full rounded-2xl overflow-hidden">
                        <img
                          src={color.image}
                          alt={`${color.name} roses`}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <span className={`text-sm font-bold ${isSelected ? 'text-blush-500' : 'text-charcoal'}`}>
                        {color.name}
                      </span>
                      {isSelected && (
                        <div className="absolute top-2 right-2 bg-blush-300 text-white p-1 rounded-full shadow-sm">
                          <SafeIcon name="Check" className="w-3 h-3" />
                        </div>
                      )}
                    </motion.button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Mobile Preview */}
        <div className="lg:hidden flex flex-col items-center gap-4 order-2">
          <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Live Preview</p>
          <VasePreview
            design={formData.design}
            roseColor={formData.flower_type}
            className="max-w-[280px] w-full shadow-glow ring-4 ring-white"
          />
          {canPreview && (
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-full border border-sage-200 text-sage-500 hover:text-blush-400 hover:border-blush-200 transition-all text-xs font-bold uppercase tracking-widest bg-white shadow-sm"
            >
              <SafeIcon name="Maximize" className="text-sm" /> View Larger
            </button>
          )}
        </div>

        {/* Desktop sticky preview */}
        <div className="hidden lg:block lg:col-span-4 sticky top-32 order-3">
          <div className="space-y-4">
            <p className="text-center text-[10px] font-bold text-sage-400 uppercase tracking-widest">Live Preview</p>
            <VasePreview
              design={formData.design}
              roseColor={formData.flower_type}
              className="shadow-glow ring-8 ring-white"
            />
            {canPreview && (
              <div className="flex justify-center">
                <button
                  onClick={() => setShowModal(true)}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-sage-200 text-sage-500 hover:text-blush-400 hover:border-blush-200 transition-all text-xs font-bold uppercase tracking-widest bg-white shadow-sm"
                >
                  <SafeIcon name="Maximize" className="text-sm" /> View Larger
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <NavButtons onNext={onNext} onBack={onBack} disabled={!isValid} nextLabel="Continue to Add-ons" />

      {/* Enlarged Preview Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowModal(false)}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white max-w-[420px] w-full rounded-[2.5rem] p-8 shadow-2xl relative"
            >
              <button
                onClick={() => setShowModal(false)}
                className="absolute top-6 right-6 p-3 bg-sage-50 hover:bg-blush-100 text-sage-400 hover:text-blush-500 rounded-full transition-all z-50 shadow-sm active:scale-90"
                aria-label="Close Preview"
              >
                <SafeIcon name="X" className="text-xl block" />
              </button>
              <div className="space-y-6">
                <div className="relative pb-8">
                  <VasePreview design={formData.design} roseColor={formData.flower_type} />
                  {/* Vase Dimensions Overlay */}
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute left-0 top-[50%] bottom-[12%] w-px bg-sage-300">
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-px bg-sage-300"></div>
                      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2 h-px bg-sage-300"></div>
                      <div className="absolute top-1/2 -translate-y-1/2 -left-1 font-bold text-[10px] text-sage-500 whitespace-nowrap -rotate-90">
                        10" HEIGHT
                      </div>
                    </div>
                    <div className="absolute bottom-4 left-[35%] right-[35%] h-px bg-sage-300">
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-px h-2 bg-sage-300"></div>
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-px h-2 bg-sage-300"></div>
                      <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 font-bold text-[10px] text-sage-500 whitespace-nowrap">
                        3.5" WIDE
                      </div>
                    </div>
                  </div>
                </div>
                <h3 className="font-serif text-2xl text-charcoal text-center">
                  {formData.design?.name}
                </h3>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
